"use client"

import { useState, useEffect, useCallback } from "react"
import type { SyncSection, SyncConfiguration } from "../types/sync-flow"
import { useSyncSections , STORAGE_KEYS } from "../constants/sync-flow"
import { useLocalStorage } from "./useLocalStorage"
import { useFormValidation } from "./useFormValidation"

export const useSyncDirection = () => {
  const baseSections = useSyncSections(); // always recomputed fresh on mount
  const [sections, setSections] = useState<SyncSection[]>([])
  const [loading, setLoading] = useState(false)

  const { getItem, setItem } = useLocalStorage()
  const { errors, setError, clearErrors, hasErrors } = useFormValidation()

  // ✅ Always refresh sections when baseSections changes (new localStorage values)
  useEffect(() => {
    setSections(baseSections)
  }, [baseSections])

  // ✅ Load saved config if exists
  useEffect(() => {
    const savedConfig = getItem(STORAGE_KEYS.SYNC_CONFIGURATION)
    if (savedConfig) {
      try {
        const config = JSON.parse(savedConfig)
        if (config.rules) {
          setSections((prevSections) =>
            prevSections.map((section) => ({
              ...section,
              rules: section.rules.map((rule) => {
                const savedRule = config.rules[section.id]?.find((r: any) => r.id === rule.id)
                return savedRule ? { ...rule, enabled: savedRule.enabled } : rule
              }),
            })),
          )
        }
      } catch (error) {
        console.error("Error loading saved configuration:", error)
      }
    }
  }, [getItem])

  const validateForm = useCallback((): boolean => {
    clearErrors()

    // Check if at least one rule is enabled
    const hasEnabledRules = sections.some((section) => section.rules.some((rule) => rule.enabled))

    if (!hasEnabledRules) {
      setError("rules", "Please enable at least one sync rule to continue")
    }

    // Check for creating rules
    const creatingRules = sections.find((s) => s.id === "creating")?.rules || []
    const enabledCreatingRules = creatingRules.filter((r) => r.enabled)

    if (enabledCreatingRules.length === 0) {
      setError("creating", "At least one creating/updating rule must be enabled")
    }

    return !hasEnabledRules || enabledCreatingRules.length === 0 ? false : true
  }, [sections, clearErrors, setError])

  const toggleSection = useCallback((sectionId: string) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId ? { ...section, expanded: !section.expanded } : section,
      ),
    )
  }, [])

  const toggleRule = useCallback(
    (sectionId: string, ruleId: string) => {
      setSections((prev) =>
        prev.map((section) =>
          section.id === sectionId
            ? {
                ...section,
                rules: section.rules.map((rule) =>
                  rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule,
                ),
              }
            : section,
        ),
      )
      clearErrors()
    },
    [clearErrors],
  )

  const saveConfiguration = useCallback(async () => {
    if (!validateForm()) return false

    setLoading(true)

    try {
      const syncConfig: SyncConfiguration = {
        sourceSystem: "Source",
        targetSystem: "Target",
        syncType: "bidirectional",
        description: "Two-way sync of HubSpot contact and contacts to Salesforce lead.",
        rules: {
          creating: sections.find((s) => s.id === "creating")?.rules || [],
          deleting: sections.find((s) => s.id === "deleting")?.rules || [],
        },
      }

      const getEnabledStates = (source: string, actionType: string[]) => {
        const matchingRules = sections
          .flatMap((s) => s.rules)
          .filter((r) => r.sourceIcon === source && actionType.some((a) => r.id.includes(a)))

        return {
          create_enabled: matchingRules.some((r) => r.id.includes("create") && r.enabled),
          update_enabled: matchingRules.some((r) => r.id.includes("update") && r.enabled),
          delete_enabled: matchingRules.some((r) => r.id.includes("delete") && r.enabled),
          restore_enabled: matchingRules.some((r) => r.id.includes("restore") && r.enabled),
        }
      }

      const mappedSyncConfig = {
        hs_to_sf: getEnabledStates("hubspot", ["create", "update", "delete"]),
        sf_to_hs: getEnabledStates("salesforce", ["create", "update", "delete"]),
      }

      const modifySyncConfig = {
        ...mappedSyncConfig,
        rules: {
          creating: sections.find((s) => s.id === "creating")?.rules || [],
          deleting: sections.find((s) => s.id === "deleting")?.rules || [],
        },
      }

      setItem(STORAGE_KEYS.MODIFY_SYNC_CONFIGURATION, JSON.stringify(modifySyncConfig))
      setItem(STORAGE_KEYS.SYNC_CONFIGURATION, JSON.stringify(syncConfig))
      setItem(STORAGE_KEYS.CONNECTOR_FLOW_STEP, "5")

      return true
    } catch (error) {
      console.error("Error saving configuration:", error)
      return false
    } finally {
      setLoading(false)
    }
  }, [sections, validateForm, setItem])

  return {
    sections,
    loading,
    errors,
    hasErrors,
    toggleSection,
    toggleRule,
    saveConfiguration,
  }
}
